/**
 * 토스트 Context
 *
 * 책임:
 * - 전역 알림 (토스트) 표시/관리
 * - 에러/성공/정보 메시지를 화면 우상단에 스택으로 표시
 * - 자동 제거 (기본 4초)
 * - 클릭으로 수동 제거
 */

import { createContext, type ReactNode, useState, useCallback, useContext } from 'react';

// 토스트 한 건의 인터페이스
export interface Toast {
  id: string; // 고유 ID (timestamp + random)
  type: 'error' | 'success' | 'info';
  message: string;
  duration?: number; // 밀리초 (기본 4000)
}

// Context에 포함된 상태 + 액션
export interface ToastContextType {
  toasts: Toast[];
  show: (type: Toast['type'], message: string, duration?: number) => void;
  remove: (id: string) => void;
}

// Context 객체 생성
export const ToastContext = createContext<ToastContextType | null>(null);

// Context Provider 컴포넌트
export interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // 토스트 추가: 고유 ID 생성 → 배열에 추가 → 자동 제거 타이머 설정
  const show = useCallback(
    (type: Toast['type'], message: string, duration = 4000) => {
      const id = `${Date.now()}-${Math.random()}`;
      const toast: Toast = { id, type, message, duration };
      setToasts((prev) => [...prev, toast]);

      // duration > 0 이면 자동 제거
      if (duration > 0) {
        setTimeout(() => {
          remove(id);
        }, duration);
      }
    },
    []
  );

  // 토스트 제거: ID로 배열에서 필터링
  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Context 값
  const value: ToastContextType = { toasts, show, remove };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

/**
 * ToastContainer 컴포넌트
 * 화면 우상단에 토스트 스택을 렌더링
 * AdminApp에서 사용 (Providers 아래)
 */
export function ToastContainer() {
  const context = useContext(ToastContext);
  if (!context) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {context.toasts.map((toast) => (
        <div
          key={toast.id}
          style={{
            padding: '1rem',
            marginBottom: '0.5rem',
            borderRadius: '0.5rem',
            // 타입별 배경색 + 글씨색
            backgroundColor:
              toast.type === 'error'
                ? '#fee' // 밝은 빨강
                : toast.type === 'success'
                  ? '#efe' // 밝은 초록
                  : '#eef', // 밝은 파랑
            color:
              toast.type === 'error'
                ? '#c00' // 진한 빨강
                : toast.type === 'success'
                  ? '#0a0' // 진한 초록
                  : '#00a', // 진한 파랑
            pointerEvents: 'auto',
            cursor: 'pointer',
            fontFamily: 'system-ui, sans-serif',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          onClick={() => context.remove(toast.id)}
        >
          {toast.message}
        </div>
      ))}
    </div>
  );
}
